'use client'

import { cn } from '@/util/cn'
import { ChangeEvent, InputHTMLAttributes, useId, useState } from 'react'

type MimeType = 'image/*' | 'video/*'
type MimeTypes = MimeType[]

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'accept'> & {
    className?: string
    maxFileSize?: number
    accept: MimeType[]
}

export const MEDIA_TYPES = {
    VIDEO: 'video',
    IMAGE: 'image',
    GIF: 'gif',
}

export const fileSizeToMB = (size = 0) => size / (1024 * 1024)

const isVideo = (type = '') => type.indexOf('video/') > -1
const isImage = (type = '') => type.indexOf('image/') > -1
const Errors = {
    NO_FILE: 'NO_FILE',
    INVALID_FILE: 'INVALID_FILE',
    FILE_TOO_BIG: 'FILE_TOO_BIG',
    FILE_TOO_LONG: 'FILE_TOO_LONG',
    ONLY_ONE_VIDEO: 'ONLY_ONE_VIDEO',
    COMBINED_VIDEO_AND_IMAGE: 'COMBINED_VIDEO_AND_IMAGE',
    ONLY_ONE_FILE: 'ONLY_ONE_FILE',
}

export const mimeTypeToFileType = (mimeType: MimeType) => {
    if (isVideo(mimeType)) {
        return MEDIA_TYPES.VIDEO
    } else if (isImage(mimeType)) {
        return MEDIA_TYPES.IMAGE
    }
}

const checkIfValid = (fileType: string, mimeTypes: MimeTypes) =>
    mimeTypes.some((mimeType) => {
        const [type, subtype] = mimeType.split('/')
        const compare = subtype === '*' ? type : mimeType
        if (fileType.indexOf(compare) > -1) {
            return true
        }
    })

const validateFile = (file: File, mimeTypes: MimeTypes, maxDuration: number, maxFilesize: number) =>
    new Promise((resolve, reject) => {
        if (!file || !file.type) {
            // TODO how do we handle this
            reject(Errors.NO_FILE)
            return
        }

        if (!checkIfValid(file.type, mimeTypes)) {
            reject(Errors.INVALID_FILE)
            return
        }
        const size = file.size ? file.size / (1024 * 1024) : 0

        // check file size. if too big, show error
        // and reset the input value so that the
        // error shows again if the same file is
        // selected.
        if (size > maxFilesize) {
            reject(Errors.FILE_TOO_BIG)
            return
        }

        // check file type
        const fileType = mimeTypeToFileType(file.type as MimeType)
        if (!fileType) {
            reject(Errors.INVALID_FILE)
            return
        }

        const result = { file, fileType, type: 'file' }

        if (maxDuration && fileType === 'video') {
            const video = document.createElement('video')
            video.onerror = (e) => {
                console.info(`Reject file:`, e)
                reject(Errors.INVALID_FILE)
            }
            video.onloadedmetadata = () => {
                if (video.duration > maxDuration) {
                    reject(Errors.FILE_TOO_LONG)
                } else {
                    resolve(result)
                }
                URL.revokeObjectURL(video.src)
            }
            video.src = URL.createObjectURL(file)
        } else {
            resolve(result)
        }
    })

const getFileLabel = (files: FileList | null, placeholder?: InputHTMLAttributes<HTMLInputElement>['placeholder']) => {
    if (!files?.length) {
        return placeholder || 'Choose File'
    }
    return files.length === 1 ? files[0].name : `${files.length} files selected`
}

const FileInput = ({
    maxFileSize = Infinity,
    className,
    accept,
    id,
    placeholder,
    onChange,
    disabled,
    ...rest
}: Props) => {
    const generatedId = useId()
    const inputId = id || generatedId
    const [fileLabel, setFileLabel] = useState(getFileLabel(null, placeholder))
    const [hasFile, setHasFile] = useState(false)

    const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
        setFileLabel(getFileLabel(e.target.files, placeholder))
        setHasFile(!!e.target.files?.length)
        onChange?.(e)
        if (e.target.files) {
            console.log('File input changed')
            for (var i = 0; i < e.target.files.length; i++) {
                const file = e.target.files[i]
                const valid = await validateFile(file, accept, 0, maxFileSize)
                if (valid) {
                    console.log('File is valid')
                } else {
                    console.log('File is invalid')
                }
            }
        }
    }

    return (
        <label
            htmlFor={inputId}
            className={cn(
                'flex w-full cursor-pointer items-center justify-between gap-3 rounded-md bg-giphyBlack px-4 py-3.5 font-bold transition-colors',
                disabled && 'pointer-events-none opacity-50',
                className
            )}
        >
            <span
                className={cn(
                    'min-w-0 flex-1 truncate text-left',
                    hasFile ? 'text-giphyWhite' : 'text-giphyLightCharcoal'
                )}
            >
                {fileLabel}
            </span>
            <span className="shrink-0 rounded bg-giphyDarkGrey px-3 py-1 text-xs text-giphyLightGrey">Browse</span>
            <input
                id={inputId}
                onChange={handleChange}
                accept={accept.join(',')}
                type="file"
                disabled={disabled}
                placeholder={placeholder}
                {...rest}
                className="sr-only"
            />
        </label>
    )
}

export default FileInput
